import type { AdminProposalSummary, ProposalStatus } from './api';

export type AdminHubFilter = 'all' | ProposalStatus;

export const ADMIN_HUB_FILTER_STORAGE_KEY = 'admin-hub-filter';

const ADMIN_HUB_FILTERS: readonly AdminHubFilter[] = [
  'all',
  'draft',
  'published',
  'signed',
  'archived',
];

type SearchableProposal = Pick<AdminProposalSummary, 'client_name' | 'slug' | 'status'>;
type StorageReader = Pick<Storage, 'getItem'>;
type StorageWriter = Pick<Storage, 'setItem'>;
type StorageReaderAccessor = () => StorageReader;
type StorageWriterAccessor = () => StorageWriter;

export function isAdminHubFilter(value: unknown): value is AdminHubFilter {
  return typeof value === 'string' && ADMIN_HUB_FILTERS.includes(value as AdminHubFilter);
}

export function restoreAdminHubFilter(getStorage: StorageReaderAccessor): AdminHubFilter {
  try {
    const storage = getStorage();
    const stored = storage.getItem(ADMIN_HUB_FILTER_STORAGE_KEY);
    return isAdminHubFilter(stored) ? stored : 'all';
  } catch {
    return 'all';
  }
}

export function persistAdminHubFilter(
  getStorage: StorageWriterAccessor,
  filter: AdminHubFilter,
): void {
  try {
    const storage = getStorage();
    storage.setItem(ADMIN_HUB_FILTER_STORAGE_KEY, filter);
  } catch {
    // The view still works when storage is unavailable or full.
  }
}

export function filterAdminProposals<T extends SearchableProposal>(
  proposals: readonly T[],
  filter: AdminHubFilter,
  search: string,
): T[] {
  const query = search.trim().toLowerCase();
  return proposals.filter((proposal) => {
    if (filter !== 'all' && proposal.status !== filter) return false;
    if (!query) return true;
    return (
      proposal.client_name.toLowerCase().includes(query) ||
      proposal.slug.toLowerCase().includes(query)
    );
  });
}
